import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import type { DealerBuyerRow } from '../../types/bruktbilfinansiering'

const _nbNok = new Intl.NumberFormat('nb-NO', { style: 'currency', currency: 'NOK', maximumFractionDigits: 0 })
const PAGE_SIZE = 50

function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  const [y, m, d] = iso.split('-')
  return `${d}.${m}.${y}`
}

function fmtMonthYear(iso: string | null): string {
  if (!iso) return '—'
  const [y, m] = iso.split('-')
  return `${m}.${y}`
}

const CONFIDENCE_STYLE: Record<string, string> = {
  HIGH: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  MEDIUM: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  LOW: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
}

const CONFIDENCE_TOOLTIP_KEYS: Record<string, 'soldRows.confidence.HIGH' | 'soldRows.confidence.MEDIUM' | 'soldRows.confidence.LOW'> = {
  HIGH: 'soldRows.confidence.HIGH',
  MEDIUM: 'soldRows.confidence.MEDIUM',
  LOW: 'soldRows.confidence.LOW',
}

type SortKey = 'omregistreringsdato' | 'merke' | 'modell' | 'registrert_forste_gang' | 'financed' | 'bank_name' | 'bank_belop' | 'tinglysingsdato' | 'loan_confidence' | 'buyer_kind' | 'buyer_kommune'

interface Props {
  rows: DealerBuyerRow[]
  totalSales: number
  financedSales: number
  contextInfo?: string
  /** The "Solgte biler" heading moved up to the tab container in #407.
   *  Left optional so any other caller keeps its own heading. */
  hideHeading?: boolean
  /** Newest date whose loan data is settled (today - 15). Shown so the user
   *  can see why this tab stops earlier than "Uten finansieringsdata". */
  loanDataThrough?: string
}

export default function DealerSoldRowsSection({ rows, totalSales: _totalSales, financedSales: _financedSales, contextInfo, hideHeading, loanDataThrough }: Props) {
  const { t } = useTranslation('bbf')
  const [page, setPage] = useState(1)

  // --- Filter state ---
  const [filterBank, setFilterBank] = useState('')
  const [filterMerke, setFilterMerke] = useState('')
  const [filterKjoper, setFilterKjoper] = useState('')
  const [filterFinansiering, setFilterFinansiering] = useState('')

  // --- Sort state ---
  const [sortCol, setSortCol] = useState<SortKey | null>(null)
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  function handleSort(col: SortKey) {
    if (sortCol === col) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortCol(col)
      setSortDir('desc')
    }
  }

  // --- Unique values for filter dropdowns ---
  const uniqueBanks = useMemo(() => [...new Set(rows.filter(r => r.bank_name).map(r => r.bank_name!))].sort(), [rows])
  const uniqueMerker = useMemo(() => [...new Set(rows.filter(r => r.merke).map(r => r.merke!))].sort(), [rows])

  // --- Apply filters ---
  const filteredRows = useMemo(() => rows
    .filter(r => !filterBank || r.bank_name === filterBank)
    .filter(r => !filterMerke || r.merke === filterMerke)
    .filter(r => !filterKjoper || r.buyer_kind === filterKjoper)
    .filter(r => !filterFinansiering || (r.financed ? 'Ja' : 'Nei') === filterFinansiering),
    [rows, filterBank, filterMerke, filterKjoper, filterFinansiering])

  // --- Apply sort ---
  const sortedRows = useMemo(() => {
    if (!sortCol) return filteredRows
    return [...filteredRows].sort((a, b) => {
      const av = a[sortCol], bv = b[sortCol]
      if (av == null && bv == null) return 0
      if (av == null) return 1
      if (bv == null) return -1
      const cmp = av < bv ? -1 : av > bv ? 1 : 0
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [filteredRows, sortCol, sortDir])

  // --- Pagination on sorted+filtered rows ---
  const totalPages = Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE))
  useEffect(() => { setPage(1) }, [rows, filterBank, filterMerke, filterKjoper, filterFinansiering, sortCol, sortDir])
  const safePage = Math.min(page, totalPages)
  const pageRows = sortedRows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)
  const rowOffset = (safePage - 1) * PAGE_SIZE

  const hasActiveFilter = !!(filterBank || filterMerke || filterKjoper || filterFinansiering)

  function SortHeader({ col, label }: { col: SortKey; label: string }) {
    return (
      <th className="py-2 pr-3 whitespace-nowrap cursor-pointer select-none" onClick={() => handleSort(col)}>
        {label} {sortCol === col ? (sortDir === 'asc' ? '\u2191' : '\u2193') : ''}
      </th>
    )
  }

  return (
    <section className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
      <header className="flex items-center justify-between mb-3">
        {hideHeading ? (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('soldRows.withLoanIntro')}{' '}
            <strong>{loanDataThrough ? fmtDate(loanDataThrough) : t('soldRows.todayMinus15')}</strong>
          </p>
        ) : (
          <h3 className="text-lg font-semibold">{t('soldRows.title')}</h3>
        )}
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {t('soldRows.financedOf', { financed: filteredRows.filter(r => r.financed).length, total: filteredRows.length })}
          {filteredRows.length < rows.length && ` (${t('soldRows.totalN', { n: rows.length })})`}
        </span>
      </header>

      {contextInfo && (
        <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg px-3 py-2 text-xs text-blue-700 dark:text-blue-300 mb-3">
          {contextInfo}
        </div>
      )}

      {/* Filter dropdowns */}
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <select value={filterBank} onChange={e => setFilterBank(e.target.value)}
          className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-sm bg-white dark:bg-gray-800">
          <option value="">{t('shared.allBanks')}</option>
          {uniqueBanks.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
        <select value={filterMerke} onChange={e => setFilterMerke(e.target.value)}
          className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-sm bg-white dark:bg-gray-800">
          <option value="">{t('shared.allBrands')}</option>
          {uniqueMerker.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <select value={filterKjoper} onChange={e => setFilterKjoper(e.target.value)}
          className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-sm bg-white dark:bg-gray-800">
          <option value="">{t('shared.allBuyers')}</option>
          <option value="person">{t('shared.privatePerson')}</option>
          <option value="org">{t('soldRows.organisation')}</option>
        </select>
        <select value={filterFinansiering} onChange={e => setFilterFinansiering(e.target.value)}
          className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-sm bg-white dark:bg-gray-800">
          <option value="">{t('shared.all')}</option>
          <option value="Ja">{t('soldRows.financed')}</option>
          <option value="Nei">{t('soldRows.notFinanced')}</option>
        </select>
        {hasActiveFilter && (
          <button onClick={() => { setFilterBank(''); setFilterMerke(''); setFilterKjoper(''); setFilterFinansiering('') }}
            className="text-xs text-primary-600 hover:underline">
            {t('shared.reset')}
          </button>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
              <SortHeader col="omregistreringsdato" label={t('shared.date')} />
              <SortHeader col="merke" label={t('shared.brand')} />
              <SortHeader col="modell" label={t('shared.model')} />
              <SortHeader col="registrert_forste_gang" label={t('shared.firstReg')} />
              <SortHeader col="financed" label={t('soldRows.cols.financing')} />
              <SortHeader col="bank_name" label={t('shared.bank')} />
              <SortHeader col="bank_belop" label={t('soldRows.cols.amount')} />
              <SortHeader col="tinglysingsdato" label={t('soldRows.cols.loanDate')} />
              <SortHeader col="loan_confidence" label={t('soldRows.cols.loanConfidence')} />
              <SortHeader col="buyer_kind" label={t('shared.buyer')} />
              {/* No buyer age or postnummer: per sale they identify the buyer (#912). */}
              <SortHeader col="buyer_kommune" label={t('shared.kommune')} />
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row, i) => (
              <tr
                key={`${row.omregistreringsdato}-${row.merke ?? ''}-${row.modell ?? ''}-${rowOffset + i}`}
                className={
                  row.financed
                    ? 'bg-green-50 dark:bg-green-900/20 border-b border-gray-100 dark:border-gray-700'
                    : 'border-b border-gray-100 dark:border-gray-700'
                }
              >
                <td className="py-1 pr-3 whitespace-nowrap">{fmtDate(row.omregistreringsdato)}</td>
                <td className="pr-3">{row.merke ?? '—'}</td>
                <td className="pr-3">{row.modell ?? '—'}</td>
                <td className="pr-3 whitespace-nowrap">{fmtMonthYear(row.registrert_forste_gang)}</td>
                <td className="pr-3">{row.financed ? t('soldRows.yes') : t('soldRows.no')}</td>
                <td className="pr-3">{row.bank_name ?? '—'}</td>
                <td className="pr-3 whitespace-nowrap">{row.bank_belop != null ? _nbNok.format(row.bank_belop) : '—'}</td>
                <td className="pr-3 whitespace-nowrap">{fmtDate(row.tinglysingsdato)}</td>
                <td className="pr-3">
                  {row.loan_confidence ? (
                    <span
                      title={CONFIDENCE_TOOLTIP_KEYS[row.loan_confidence] ? t(CONFIDENCE_TOOLTIP_KEYS[row.loan_confidence]) : undefined}
                      className={`inline-block px-1.5 py-0.5 rounded-sm text-xs font-medium cursor-help ${CONFIDENCE_STYLE[row.loan_confidence] ?? ''}`}
                    >
                      {row.loan_confidence}
                    </span>
                  ) : '—'}
                </td>
                <td className="pr-3">{buyerKindLabel(row, t)}</td>
                <td>{row.buyer_kommune ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3 text-sm text-gray-600 dark:text-gray-400">
          <span>
            {t('shared.pagination', { page: safePage, total: totalPages, from: rowOffset + 1, to: Math.min(safePage * PAGE_SIZE, sortedRows.length), rows: sortedRows.length })}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={safePage === 1}
              className="px-3 py-1 rounded-sm border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              {t('shared.prev')}
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={safePage === totalPages}
              className="px-3 py-1 rounded-sm border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              {t('shared.next')}
            </button>
          </div>
        </div>
      )}
    </section>
  )
}

function buyerKindLabel(row: DealerBuyerRow, t: TFunction<'bbf'>): string {
  if (row.buyer_kind === 'org') return row.buyer_org_navn ?? t('soldRows.orgFallback')
  if (row.buyer_kind === 'person') return t('shared.privatePerson')
  return '—'
}
